import React, { useEffect, useState } from 'react';
import axios from 'axios';

import Text from 'v3/components/Text';
import { useSnackbar } from 'v3/components/Snackbar';

import { postMicrosoftLogin } from 'services/api';

import microsoftIdentityProvider from './microsoftIdentityProvider';

const MsAuthStatus = {
  IDLE: 'idle',
  AUTHENTICATING_MICROSOFT: 'authenticatingMicrosoft',
  AUTHENTICATING_BACKEND: 'authenticatingBackend',
};

/**
 * Owns the "Entrar com Microsoft" auth flow (RF02-RF09): starting the Microsoft
 * redirect, resuming it on return, exchanging the resulting ID token with the
 * backend, and surfacing friendly errors for both Microsoft-side and
 * backend-side failures. Exists so the login page only renders UI and never
 * touches MSAL or the auth-flow state machine directly.
 */
function useMicrosoftLogin() {
  const snackbar = useSnackbar();
  const [status, setStatus] = useState(MsAuthStatus.IDLE);

  useEffect(() => {
    async function resumeMicrosoftLogin() {
      let redirectResult;

      try {
        await microsoftIdentityProvider.initialize();
        redirectResult = await microsoftIdentityProvider.handleRedirectResult();
      } catch (ex) {
        snackbar.show(
          <Text>
            Não foi possível concluir o login com a Microsoft. Tente novamente.
          </Text>,
          {
            type: 'error',
          }
        );
        return;
      }

      if (!redirectResult) {
        return;
      }

      setStatus(MsAuthStatus.AUTHENTICATING_BACKEND);

      try {
        const session = await postMicrosoftLogin(redirectResult.idToken);

        localStorage.setItem('token', session.accessToken);

        axios.defaults.headers.common.Authorization = `Bearer ${session.accessToken}`;

        window.location.href = '/';
      } catch (ex) {
        microsoftIdentityProvider.clearInteractionState();
        snackbar.show(<Text>{ex.message}</Text>, { type: 'error' });
        setStatus(MsAuthStatus.IDLE);
      }
    }

    resumeMicrosoftLogin();
    // eslint-disable-next-line
  }, []);

  async function login() {
    setStatus(MsAuthStatus.AUTHENTICATING_MICROSOFT);

    try {
      await microsoftIdentityProvider.login();
    } catch (ex) {
      snackbar.show(
        <Text>
          Não foi possível iniciar o login com a Microsoft. Tente novamente.
        </Text>,
        {
          type: 'error',
        }
      );
      setStatus(MsAuthStatus.IDLE);
    }
  }

  return {
    loading: status !== MsAuthStatus.IDLE,
    login,
  };
}

export default useMicrosoftLogin;
